import { Injectable } from '@angular/core'
import { Action, Store } from '@ngrx/store'
import { IdentityTypeEnum } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.models'
import { GbBaseFacade } from 'libs/shared/data-access-core/src/lib/+state/gb-base-facade-interface'
import { map } from 'rxjs'
import * as AuctionSelector from './auction.selectors'
import { MenuItemTypeEnum } from './menu-items.models'
import { RootState } from './auction.selectors'

/**
 * Auction Service that will do all the dispatch and business logic. WOW
 */
@Injectable({
  providedIn: 'root',
})
export class AuctionFacade implements GbBaseFacade {
  auction$ = this.store.select(AuctionSelector.selectAuction)
  loaded$ = this.store.select(AuctionSelector.getAuctionLoaded)
  auctionUrlName$ = this.store.select(AuctionSelector.selectAuctionUrlName)
  auctionStatus$ = this.store.select(AuctionSelector.selectAuctionStatus)
  auctionLogo$ = this.store.select(AuctionSelector.selectAuctionLogo)
  auctionTitle$ = this.store.select(AuctionSelector.selectAuctionTitle)
  auctionCurrency$ = this.store.select(AuctionSelector.selectAuctionCurrency)
  auctionPaymentGateway$ = this.store.select(AuctionSelector.selectAuctionPaymentGateway)
  auctionStripeConnectUuid$ = this.store.select(AuctionSelector.selectAuctionStripeConnectUuid)
  auctionCountry$ = this.store.select(AuctionSelector.selectAuctionCountry)
  stripeConnect$ = this.store.select(AuctionSelector.selectStripeConnect)
  enableActivityFeed$ = this.store.select(AuctionSelector.selectEnableActivityFeed)
  enableItemActivityFeed$ = this.store.select(AuctionSelector.selectEnableItemActivityFeed)
  itemCount$ = this.store.select(AuctionSelector.selectItemCount)
  isSingleItemAuction$ = this.store.select(AuctionSelector.selectIsSingleItem)
  singleItemUuid$ = this.store.select(AuctionSelector.selectSingleItemUuid)
  isSingleTicketPackage$ = this.store.select(AuctionSelector.selectIsSingleTicketPackage)
  singleTicketPackageUuid$ = this.store.select(AuctionSelector.selectSingleTicketPackageUuid)
  registrationFields$ = this.store.select(AuctionSelector.registrationFields)
  termsAndConditions$ = this.store.select(AuctionSelector.termsAndConditions)
  captureTermsAndConditions$ = this.store.select(AuctionSelector.captureTermsAndConditions)
  capturePhoneNumbers$ = this.store.select(AuctionSelector.capturePhoneNumbers)
  isSaas$ = this.store.select(AuctionSelector.isSaas)
  commissionEnabled$ = this.store.select(AuctionSelector.commissionEnabled)
  tippingFeeEnabled$ = this.store.select(AuctionSelector.tippingFeeEnabled)
  tippingComponent$ = this.store.select(AuctionSelector.tippingComponent)
  tippingFeeOptions$ = this.store.select(AuctionSelector.tippingFeeOptions)
  defaultTippingFeeOption$ = this.store.select(AuctionSelector.defaultTippingFeeOption)
  displaySocialLogin$ = this.store.select(AuctionSelector.displaySocialLogin)
  facebookLoginEnabled$ = this.store.select(AuctionSelector.facebookLoginEnabled)
  googleLoginEnabled$ = this.store.select(AuctionSelector.googleLoginEnabled)
  stripeUserId$ = this.store.select(AuctionSelector.stripeUserId)
  isStripeExpress$ = this.store.select(AuctionSelector.isStripeExpress)
  requiresAdditionalInfo$ = this.store.select(AuctionSelector.requiresAdditionalInfo)
  giftAidEnabled$ = this.store.select(AuctionSelector.giftAidEnabled)
  auctionScheduledEnd$ = this.store.select(AuctionSelector.auctionScheduledEnd)
  allCustomFields$ = this.store.select(AuctionSelector.getAllCustomFields)
  categoriesMenuItemLabel$ = this.store.select(
    AuctionSelector.getMenuItemLabelByType(MenuItemTypeEnum.SearchAndCategories),
  )
  allItemsMenuItemLabel$ = this.store.select(AuctionSelector.getMenuItemLabelByType(MenuItemTypeEnum.AllItems))
  myAccountMenuItemLabel$ = this.store.select(AuctionSelector.getMenuItemLabelByType(MenuItemTypeEnum.MyAccount))
  menuItemIsVisible$ = (type: MenuItemTypeEnum) => this.store.select(AuctionSelector.getMenuItemIsVisible(type))
  identityType$ = this.auction$.pipe(
    map(({ allow_email_registration, allow_phone_registration }) => {
      if (allow_email_registration && !allow_phone_registration) {
        return IdentityTypeEnum.Email
      } else if (!allow_email_registration && allow_phone_registration) {
        return IdentityTypeEnum.Mobile
      }
      return IdentityTypeEnum.Both
    }),
  )
  allCategories$ = this.store.select(AuctionSelector.getAllCategories)
  hasCategoryWithItems$ = this.store.select(AuctionSelector.getHasCategoryWithItems)
  ticketsLabel$ = this.store.select(AuctionSelector.getTicketsLabel)
  allTaxes$ = this.store.select(AuctionSelector.getAllTaxes)
  paymentMethodsByAuthProfile$ = this.store.select(AuctionSelector.getPaymentMethodsByAuthProfile)
  selectedPaymentMethod$ = this.store.select(AuctionSelector.getSelectedPaymentMethod)
  applyFeeToFinalCharge$ = this.store.select(AuctionSelector.applyFeeToFinalCharge)
  paymentMethodFlatFee$ = this.store.select(AuctionSelector.paymentMethodFlatFee)
  paymentMethodPercentageFee$ = this.store.select(AuctionSelector.paymentMethodPercentageFee)
  showTippingMessage$ = this.store.select(AuctionSelector.showTippingMessage)
  showEventTicketsPreview$ = this.store.select(AuctionSelector.showEventTicketsPreview)
  isEventTicketingEnabled$ = this.store.select(AuctionSelector.isEventTicketingEnabled)
  eventTicketsRemaining$ = this.store.select(AuctionSelector.eventTicketsRemaining)
  passwordComplexityPolicy$ = this.store.select(AuctionSelector.getPasswordComplexityPolicy$)
  menuItems$ = this.store.select(AuctionSelector.selectAuctionMenuItems)
  paymentDonationComponent$ = this.store.select(AuctionSelector.paymentDonationComponent)
  paymentDonationSliderAmounts$ = this.store.select(AuctionSelector.paymentDonationSliderAmounts)
  paymentDonationButtonAmounts$ = this.store.select(AuctionSelector.paymentDonationButtonAmounts)

  getTaxByUuid$ = (uuid: string) => this.store.select(AuctionSelector.getTaxByUuid(uuid))
  getMenuItemByType$ = (type: MenuItemTypeEnum) => this.store.select(AuctionSelector.getMenuItemByType(type))

  constructor(private store: Store<RootState>) {}

  dispatch(action: Action) {
    this.store.dispatch(action)
  }
}
